import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertCircle,
  Search,
  Filter,
  Eye,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Star,
  Package,
  X,
  Ban,
  FileText
} from 'lucide-react';
import { collection, getDocs, query, where, orderBy, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import { ToastContainer } from '../../../components/Toast';

interface ServiceProvider {
  id: string;
  providerId: string; // Unique provider ID
  name: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  specialization: string[];
  rating: number;
  totalEarnings: number;
  completedServices: number;
  activeServices: number;
  cancelledServices: number;
  ignoredServices: number;
  joinedDate: string;
  status: 'active' | 'inactive' | 'suspended';
  profileImage?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  experience?: string;
  experienceYears?: number;
  bio?: string;
  description?: string;
  education?: string;
  skills?: string[];
  languages?: string[];
  availability?: string;
  workingHours?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  upiId?: string;
  isVerified?: boolean;
  verifiedAt?: string;
  rejectionReason?: string;
  registrationFeePaid?: boolean;
  registrationFeeAmount?: number;
  registrationFeePaidDate?: string;
  registrationFeeTransactionId?: string;
  documents?: {
    idProof?: string;
    certification?: string;
    aadharFront?: string;
    aadharBack?: string;
    panCard?: string;
    bankProof?: string;
    educationCertificate?: string;
    experienceCertificate?: string;
    policeClearance?: string;
  };
  completedServicesDetails?: any[];
  acceptedServicesDetails?: any[];
  cancelledServicesDetails?: any[];
  ignoredRequestsDetails?: any[];
  selectedServices?: string[];
  services?: string[];
}

interface ServiceHistory {
  orderId: string;
  serviceName: string;
  customerName: string;
  status: 'completed' | 'active' | 'cancelled' | 'ignored';
  amount: number;
  date: string;
  paymentStatus: 'paid' | 'pending' | 'failed';
  cancelReason?: string;
  additionalServices?: Array<{
    name: string;
    price: number;
    status: string;
  }>;
}

interface ProviderReview {
  id: string;
  providerId: string;
  orderId: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  rating: number;
  review: string;
  serviceType: string;
  createdAt: string;
  isVerified: boolean;
  response?: string;
  respondedAt?: string;
  status: 'active' | 'hidden' | 'flagged';
  flagReason?: string;
}

export default function ServiceProvidersView() {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [serviceHistory, setServiceHistory] = useState<ServiceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'suspended'>('all');
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [providerReviews, setProviderReviews] = useState<ProviderReview[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [resolvedServiceNames, setResolvedServiceNames] = useState<{[key: string]: string}>({});
  const toast = useToast();

  useEffect(() => {
    fetchProviders();
  }, []);

  // Helper function to resolve service names from IDs
  const resolveServiceNames = async (serviceIds: string[]) => {
    const resolved: {[key: string]: string} = {};
    
    for (const serviceId of serviceIds) {
      if (!serviceId || resolved[serviceId]) continue;
      
      try {
        const serviceDoc = await getDoc(doc(db, 'services', serviceId));
        if (serviceDoc.exists()) {
          const serviceData = serviceDoc.data();
          resolved[serviceId] = serviceData.name || serviceData.title || serviceId;
        } else {
          resolved[serviceId] = serviceId;
        }
      } catch (err) {
        console.error('Error resolving service name:', serviceId, err);
        resolved[serviceId] = serviceId;
      }
    }
    
    return resolved;
  };

  // Function to update provider status
  const updateProviderStatus = async (providerId: string, newStatus: string, isVerified?: boolean) => {
    try {
      setUpdatingStatus(true);
      
      const updateData: any = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };
      
      if (isVerified !== undefined) {
        updateData.isVerified = isVerified;
        updateData.verifiedAt = serverTimestamp();
      }
      
      await updateDoc(doc(db, 'serviceProviders', providerId), updateData);
      
      toast.success(`Provider status updated to ${newStatus}`);
      
      // Refresh providers list
      await fetchProviders();
      
      // Update selected provider if it's the current one
      if (selectedProvider && selectedProvider.id === providerId) {
        setSelectedProvider({
          ...selectedProvider,
          status: newStatus as 'active' | 'inactive' | 'suspended'
        });
      }
    } catch (error) {
      console.error('Error updating provider status:', error);
      toast.error('Failed to update provider status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Comprehensive analysis function for all service providers
  const analyzeAllServiceProviders = async () => {
    try {
      console.clear();
      console.log('\n' + '='.repeat(100));
      console.log('🔍 COMPREHENSIVE SERVICE PROVIDERS ANALYSIS');
      console.log('='.repeat(100) + '\n');

      // Fetch all providers
      const providersSnapshot = await getDocs(collection(db, 'serviceProviders'));
      console.log(`📊 Total Service Providers in Database: ${providersSnapshot.size}\n`);

      // Fetch all orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      console.log(`📦 Total Orders in Database: ${ordersSnapshot.size}\n`);

      const allOrders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Analyze each provider
      providersSnapshot.docs.forEach((providerDoc, index) => {
        const provider = providerDoc.data();
        const providerId = providerDoc.id;

        console.log('\n' + '─'.repeat(100));
        console.log(`👤 PROVIDER ${index + 1}/${providersSnapshot.size}: ${provider.name || provider.fullName || 'Unknown'}`);
        console.log('─'.repeat(100));

        // Basic Information
        console.log('\n📋 BASIC INFORMATION:');
        console.log(`  ID: ${providerId}`);
        console.log(`  Name: ${provider.name || provider.fullName || 'N/A'}`);
        console.log(`  Email: ${provider.email || 'N/A'}`);
        console.log(`  Phone: ${provider.phone || 'N/A'}`);
        console.log(`  Alternate Phone: ${provider.alternatePhone || 'N/A'}`);

        // Location
        console.log('\n📍 LOCATION:');
        console.log(`  Address: ${provider.address || 'N/A'}`);
        console.log(`  City: ${provider.city || 'N/A'}`);
        console.log(`  State: ${provider.state || 'N/A'}`);
        console.log(`  Pincode: ${provider.pincode || 'N/A'}`);

        // Verification Status
        console.log('\n✅ VERIFICATION STATUS:');
        console.log(`  Is Verified: ${provider.isVerified ? '✓ Yes' : '✗ No'}`);
        console.log(`  Email Verified: ${provider.isEmailVerified ? '✓ Yes' : '✗ No'}`);
        console.log(`  Phone Verified: ${provider.isPhoneVerified ? '✓ Yes' : '✗ No'}`);
        console.log(`  Account Status: ${provider.status || 'N/A'}`);

        // Services Offered
        console.log('\n🔧 SERVICES OFFERED:');
        const services = provider.selectedServices || provider.services || provider.specialization || [];
        if (services.length > 0) {
          services.forEach((service: string) => console.log(`  • ${service}`));
        } else {
          console.log('  None registered');
        }

        // Payment Information
        console.log('\n💳 PAYMENT INFORMATION:');
        console.log(`  Bank Name: ${provider.bankName || 'N/A'}`);
        console.log(`  Account Number: ${provider.accountNumber || 'N/A'}`);
        console.log(`  IFSC Code: ${provider.ifscCode || 'N/A'}`);
        console.log(`  Account Holder: ${provider.accountHolderName || 'N/A'}`);

        // Documents Status
        console.log('\n📄 DOCUMENTS:');
        const docs = provider.documents || {};
        console.log(`  Aadhaar Front: ${docs.aadharFront || provider.addharfront ? '✓ Uploaded' : '✗ Not uploaded'}`);
        console.log(`  Aadhaar Back: ${docs.aadharBack || provider.addharback ? '✓ Uploaded' : '✗ Not uploaded'}`);
        console.log(`  PAN Card: ${docs.panCard ? '✓ Uploaded' : '✗ Not uploaded'}`);

        // Firebase Document Stats
        console.log('\n📊 STATS FROM FIREBASE DOCUMENT:');
        console.log(`  Total Earnings: ₹${provider.totalEarnings || 0}`);
        console.log(`  Completed Services: ${provider.completedServiceCount || 0}`);
        console.log(`  Cancellations: ${provider.cancellationCount || 0}`);
        console.log(`  Average Rating: ${provider.averageRating || 0}`);
        console.log(`  Total Ratings: ${provider.totalRatings || 0}`);

        // Find orders for this provider - check both acceptedBy and serviceProviderId
        const providerOrders = allOrders.filter((order: any) => 
          order.acceptedBy === providerId || order.serviceProviderId === providerId
        );
        
        console.log(`\n📦 ORDERS ASSOCIATED WITH THIS PROVIDER: ${providerOrders.length}`);

        if (providerOrders.length > 0) {
          // Calculate stats from orders
          let completedCount = 0;
          let acceptedCount = 0;
          let cancelledCount = 0;
          let totalEarnings = 0;
          let totalRating = 0;
          let ratingCount = 0;

          console.log('\n  📋 Order Details:');
          providerOrders.forEach((order: any, idx: number) => {
            console.log(`\n  Order ${idx + 1}:`);
            console.log(`    Order ID: ${order.id}`);
            console.log(`    Service: ${order.serviceName || 'N/A'}`);
            console.log(`    Status: ${order.status}`);
            console.log(`    Amount: ₹${order.totalAmount || 0}`);
            console.log(`    Customer: ${order.userName || 'N/A'}`);
            console.log(`    Rating: ${order.rating ? `${order.rating}/5 ⭐` : 'Not rated'}`);

            // Count by status
            if (order.status === 'completed') {
              completedCount++;
              totalEarnings += order.totalAmount || 0;
              if (order.rating) {
                totalRating += order.rating;
                ratingCount++;
              }
            } else if (order.status === 'accepted' || order.status === 'confirmed') {
              acceptedCount++;
            } else if (order.status === 'cancelled') {
              cancelledCount++;
            }
          });

          const avgRating = ratingCount > 0 ? (totalRating / ratingCount).toFixed(1) : 'N/A';

          console.log('\n📊 CALCULATED STATS FROM ORDERS:');
          console.log(`  Total Orders: ${providerOrders.length}`);
          console.log(`  Completed: ${completedCount}`);
          console.log(`  Active/Accepted: ${acceptedCount}`);
          console.log(`  Cancelled: ${cancelledCount}`);
          console.log(`  Total Earnings: ₹${totalEarnings}`);
          console.log(`  Average Rating: ${avgRating}`);
          console.log(`  Total Ratings: ${ratingCount}`);

          // Comparison
          console.log('\n⚠️  COMPARISON (Firebase Document vs Calculated from Orders):');
          console.log(`  Earnings: ₹${provider.totalEarnings || 0} (Firebase) vs ₹${totalEarnings} (Calculated) ${(provider.totalEarnings || 0) !== totalEarnings ? '❌ MISMATCH!' : '✅ Match'}`);
          console.log(`  Completed: ${provider.completedServiceCount || 0} (Firebase) vs ${completedCount} (Calculated) ${(provider.completedServiceCount || 0) !== completedCount ? '❌ MISMATCH!' : '✅ Match'}`);
          console.log(`  Cancelled: ${provider.cancellationCount || 0} (Firebase) vs ${cancelledCount} (Calculated) ${(provider.cancellationCount || 0) !== cancelledCount ? '❌ MISMATCH!' : '✅ Match'}`);

          if ((provider.totalEarnings || 0) !== totalEarnings || 
              (provider.completedServiceCount || 0) !== completedCount ||
              (provider.cancellationCount || 0) !== cancelledCount) {
            console.log('\n🔴 SYNC ISSUE DETECTED: Firebase document stats do not match actual order data!');
            console.log('   Recommendation: Implement automatic sync or update mechanism.');
          }
        } else {
          console.log('  No orders found for this provider.');
        }

        // All Firebase Fields
        console.log('\n🗂️  ALL FIREBASE DOCUMENT FIELDS:');
        const fields = Object.keys(provider).sort();
        fields.forEach(field => {
          let value = provider[field];
          if (value && typeof value === 'object' && value.toDate) {
            value = value.toDate().toLocaleString();
          } else if (Array.isArray(value)) {
            value = `[${value.length} items]`;
          } else if (typeof value === 'object' && value !== null) {
            value = '{...}';
          }
          console.log(`  ${field}: ${value}`);
        });
      });

      console.log('\n' + '='.repeat(100));
      console.log('✅ ANALYSIS COMPLETE');
      console.log('='.repeat(100));
      console.log(`\n📊 Summary:`);
      console.log(`   • Total Providers: ${providersSnapshot.size}`);
      console.log(`   • Total Orders: ${ordersSnapshot.size}`);
      
      const providersWithOrders = providersSnapshot.docs.filter(doc => 
        allOrders.some((order: any) => order.serviceProviderId === doc.id)
      ).length;
      console.log(`   • Providers with Orders: ${providersWithOrders}`);
      console.log(`   • Providers without Orders: ${providersSnapshot.size - providersWithOrders}`);
      console.log('\n');

      toast.success('Analysis complete! Check browser console for detailed report.');
    } catch (error) {
      console.error('Error analyzing service providers:', error);
      toast.error('Failed to analyze service providers');
    }
  };

  const fetchProviders = async () => {
    try {
      setLoading(true);
      
      // Fetch all orders to calculate statistics
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      
      // Fetch from Firebase serviceProviders collection
      const providersSnapshot = await getDocs(collection(db, 'serviceProviders'));
      
      if (providersSnapshot.empty) {
        setProviders([]);
        toast.error('No service providers found in the system');
      } else {
        // Map Firebase data to ServiceProvider interface with calculated stats
        const providersData = await Promise.all(
          providersSnapshot.docs.map(async (doc) => {
            const data = doc.data();
            const providerId = doc.id;
            
            // Helper to convert Timestamp to string
            const getDateString = (dateField: any): string => {
              if (!dateField) return new Date().toISOString();
              if (dateField.toDate) return dateField.toDate().toISOString();
              if (typeof dateField === 'string') return dateField;
              return new Date().toISOString();
            };
            
            // Calculate actual statistics from orders
            // FIX: Check both acceptedBy (new field) and serviceProviderId (old field) for compatibility
            const providerOrders = ordersSnapshot.docs.filter(orderDoc => {
              const orderData = orderDoc.data();
              return orderData.acceptedBy === providerId || orderData.serviceProviderId === providerId;
            });
            
            let completedServices = 0;
            let acceptedServices = 0; // Changed from activeServices to acceptedServices (confirmed orders)
            let cancelledServices = 0;
            let ignoredServices = 0;
            let totalEarnings = 0;
            let totalRating = 0;
            let ratingCount = 0;
            
            // Count ignored services (where provider was assigned but didn't accept)
            const allOrders = ordersSnapshot.docs;
            allOrders.forEach(orderDoc => {
              const orderData = orderDoc.data();
              // Check if this provider was offered the service but didn't accept
              if (orderData.offeredProviders && Array.isArray(orderData.offeredProviders)) {
                // FIX: Check both acceptedBy and serviceProviderId
                const wasAcceptedByThisProvider = orderData.acceptedBy === providerId || orderData.serviceProviderId === providerId;
                if (orderData.offeredProviders.includes(providerId) && !wasAcceptedByThisProvider) {
                  ignoredServices++;
                }
              }
            });
            
            providerOrders.forEach(orderDoc => {
              const orderData = orderDoc.data();
              const status = orderData.status?.toLowerCase()?.trim();
              
              // Count by status - COMPREHENSIVE STATUS MATCHING
              // Completed statuses
              if (status === 'completed' || status === 'complete' || status === 'finished' || status === 'done') {
                completedServices++;
                // Add to earnings only for completed services
                const amount = typeof orderData.totalAmount === 'number' ? orderData.totalAmount : 
                              typeof orderData.payment?.total === 'number' ? orderData.payment.total :
                              typeof orderData.amount === 'number' ? orderData.amount : 0;
                totalEarnings += amount;
                
                // Calculate rating if exists
                if (orderData.rating && typeof orderData.rating === 'number') {
                  totalRating += orderData.rating;
                  ratingCount++;
                }
              } 
              // Accepted/Active statuses (provider accepted the request)
              else if (
                status === 'confirmed' || 
                status === 'accepted' || 
                status === 'active' || 
                status === 'in-progress' || 
                status === 'in_progress' ||
                status === 'inprogress' ||
                status === 'ongoing' ||
                status === 'processing'
              ) {
                acceptedServices++;
              } 
              // Cancelled statuses
              else if (
                status === 'cancelled' || 
                status === 'canceled' || 
                status === 'rejected' ||
                status === 'declined' ||
                status === 'failed'
              ) {
                cancelledServices++;
              }
            });
            
            // Don't override with provider's own arrays - use order-based counts as source of truth
            // Only supplement with provider-specific ignored requests
            const providerIgnoredRequests = data.ignoredRequests || [];
            if (Array.isArray(providerIgnoredRequests)) {
              ignoredServices = Math.max(ignoredServices, providerIgnoredRequests.length);
            }
            
            const calculatedRating = data.rating || (ratingCount > 0 ? totalRating / ratingCount : 0);
            
            // Get service offerings from provider data - check selectedServices first
            const serviceOfferings = data.selectedServices || data.services || data.specialization || [];
            
            // Resolve service names from IDs if they are service IDs (alphanumeric strings)
            let resolvedServiceNames: string[] = [];
            if (Array.isArray(serviceOfferings) && serviceOfferings.length > 0) {
              const namesMap = await resolveServiceNames(serviceOfferings);
              resolvedServiceNames = serviceOfferings.map(id => namesMap[id] || id);
            }
            
            // Handle documents with different casing and root level fields
            // Check root level first (as seen in Firebase: aadhaarBack, aadhaarFront, etc.)
            const documents = data.documents || {};
            
            // Priority: Check Firebase field names from screenshot first
            let aadharFrontUrl = data.aadhaarFront;  // From your screenshot
            let aadharBackUrl = data.aadhaarBack;    // From your screenshot
            
            // Fallback to other possible field names
            if (!aadharFrontUrl) {
              aadharFrontUrl = data.addharfront || data.aadharFront || 
                              documents.aadharFront || documents.aadhaarFront || 
                              documents.addharfront || documents.aadharfront || 
                              data.aadhaarCardFront || data.aadhaarCardFrontURL;
            }
            
            if (!aadharBackUrl) {
              aadharBackUrl = data.addharback || data.aadharBack || 
                             documents.aadharBack || documents.aadhaarBack || 
                             documents.addharback || documents.aadharback ||
                             data.aadhaarCardBack || data.aadhaarCardBackURL;
            }
            
            const normalizedDocuments = {
              aadharFront: aadharFrontUrl,
              aadharBack: aadharBackUrl,
              idProof: data.idProof || documents.idProof || documents.idproof || null,
              certification: data.certification || documents.certification || null,
              panCard: data.panCard || documents.panCard || documents.pancard || null
            };
            
            return {
              id: providerId,
              providerId: data.providerId || `SP-${providerId.substring(0, 8).toUpperCase()}`, // Generate readable provider ID
              name: data.name || 'N/A',
              email: data.email || 'N/A',
              phone: data.phone || 'N/A',
              alternatePhone: data.alternatePhone || data.alternate_phone || null,
              specialization: resolvedServiceNames,
              rating: typeof calculatedRating === 'number' ? calculatedRating : 0,
              totalEarnings: totalEarnings, // ALWAYS use calculated value from orders
              completedServices: completedServices, // ALWAYS use calculated value from orders
              activeServices: acceptedServices, // Now shows confirmed/accepted services
              cancelledServices: cancelledServices,
              ignoredServices: ignoredServices,
              joinedDate: getDateString(data.joinedDate || data.createdAt),
              status: data.status || 'inactive', // Default to inactive for new accounts
              profileImage: data.profileImage || data.profilePicture || null,
              address: data.address || null,
              city: data.city || null,
              state: data.state || null,
              pincode: data.pincode || data.zipCode || data.zip_code || null,
              experience: data.experience || null,
              experienceYears: data.experienceYears || data.experience_years || null,
              bio: data.bio || data.description || null,
              description: data.description || null,
              education: data.education || null,
              skills: data.skills || [],
              languages: data.languages || [],
              availability: data.availability || null,
              workingHours: data.workingHours || data.working_hours || null,
              bankName: data.bankName || data.bank_name || null,
              accountNumber: data.accountNumber || data.account_number || null,
              ifscCode: data.ifscCode || data.ifsc_code || null,
              upiId: data.upiId || data.upi_id || null,
              isVerified: data.isVerified || false,
              verifiedAt: data.verifiedAt ? getDateString(data.verifiedAt) : null,
              rejectionReason: data.rejectionReason || data.rejection_reason || null,
              // Registration Fee - Check multiple possible field names and formats
              registrationFeePaid: Boolean(
                data.registrationFeePaid || 
                data.registration_fee_paid || 
                data.registrationfee_paid ||
                data.isRegistrationFeePaid ||
                data.feeStatus === 'paid' ||
                data.feeStatus === 'Paid' ||
                false
              ),
              registrationFeeAmount: 
                data.registrationFeeAmount || 
                data.registration_fee_amount || 
                data.registrationfee || 
                data.registrationFee ||
                data.feeAmount ||
                39, // Default ₹39
              registrationFeePaidDate: 
                data.registrationFeePaidDate ? getDateString(data.registrationFeePaidDate) : 
                data.registration_fee_paid_date ? getDateString(data.registration_fee_paid_date) :
                data.feePaidDate ? getDateString(data.feePaidDate) :
                data.feeDate ? getDateString(data.feeDate) :
                null,
              registrationFeeTransactionId: 
                data.registrationFeeTransactionId || 
                data.registration_fee_transaction_id || 
                data.feeTransactionId ||
                data.transactionId ||
                data.txnId ||
                null,
              documents: normalizedDocuments,
              completedServicesDetails: data.completedServices || [],
              acceptedServicesDetails: data.acceptedServices || [],
              cancelledServicesDetails: data.cancelledServices || [],
              ignoredRequestsDetails: data.ignoredRequests || [],
              selectedServices: data.selectedServices || [],
              services: data.services || []
            } as ServiceProvider;
          })
        );
        
        setProviders(providersData);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to fetch service providers from Firebase');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceHistory = async (providerId: string) => {
    try {
      setLoadingHistory(true);
      
      // Fetch the provider document to get their service arrays
      const providerDoc = await getDoc(doc(db, 'serviceProviders', providerId));
      
      if (!providerDoc.exists()) {
        setServiceHistory([]);
        return;
      }
      
      const providerData = providerDoc.data();
      const historyData: ServiceHistory[] = [];
      
      // Helper to convert Timestamp to string
      const getDateString = (dateField: any): string => {
        if (!dateField) return new Date().toISOString();
        if (dateField.toDate) return dateField.toDate().toISOString();
        if (typeof dateField === 'string') return dateField;
        return new Date().toISOString();
      };

      // Helper to resolve service name from service ID
      const resolveServiceName = async (serviceId: string | undefined): Promise<string> => {
        if (!serviceId) return 'N/A';
        try {
          const serviceDoc = await getDoc(doc(db, 'services', serviceId));
          if (serviceDoc.exists()) {
            return serviceDoc.data().name || serviceDoc.data().title || 'Service';
          }
        } catch (err) {
          console.error('Error resolving service name:', err);
        }
        return 'Service';
      };

      // Helper to fetch complete order data with all details
      const fetchCompleteOrderData = async (orderId: string): Promise<any | null> => {
        try {
          const orderDoc = await getDoc(doc(db, 'orders', orderId));
          if (!orderDoc.exists()) return null;
          
          const orderData = orderDoc.data();
          
          // Try multiple paths to get service name
          let serviceName = orderData.serviceName || 
                          orderData.items?.[0]?.name || 
                          orderData.service?.name;
          
          // If serviceId exists but no service name, resolve it
          if (!serviceName && orderData.serviceId) {
            serviceName = await resolveServiceName(orderData.serviceId);
          }
          
          // Try multiple paths for customer name
          const customerName = orderData.customerName || 
                              orderData.userName || 
                              orderData.user?.name ||
                              orderData.address?.fullName || 
                              orderData.address?.name ||
                              orderData.customer?.name ||
                              'Unknown Customer';
          
          // Try multiple paths for amount
          const amount = orderData.totalAmount || 
                        orderData.payment?.total || 
                        orderData.payment?.amount ||
                        orderData.total ||
                        orderData.price ||
                        0;
          
          return {
            ...orderData,
            serviceName,
            customerName,
            totalAmount: amount,
            createdAt: orderData.createdAt,
            payment: orderData.payment,
            paymentStatus: orderData.paymentStatus,
            additionalServices: orderData.additionalServices,
            cancelReason: orderData.cancelReason
          };
        } catch (err) {
          console.error('Error fetching order data:', orderId, err);
          return null;
        }
      };
      
      // 1. Add completed services from provider's completedServices array
      if (providerData.completedServices && Array.isArray(providerData.completedServices)) {
        for (const service of providerData.completedServices) {
          try {
            let orderData: any = null;
            let serviceName = service.serviceName || 'Service';
            let customerName = service.customerName || 'Unknown';
            let amount = (service.baseAmount || 0) + (service.additionalAmount || 0);
            
            // If orderId exists, fetch complete order details
            if (service.orderId) {
              orderData = await fetchCompleteOrderData(service.orderId);
              if (orderData) {
                serviceName = orderData.serviceName || serviceName;
                customerName = orderData.customerName || customerName;
                // Use order amount if service amount is 0
                if (amount === 0 && orderData.totalAmount) {
                  amount = orderData.totalAmount;
                }
              }
            }
            
            // If still no service name and serviceId exists, resolve it
            if (serviceName === 'Service' && service.serviceId) {
              serviceName = await resolveServiceName(service.serviceId);
            }
            
            historyData.push({
              orderId: service.orderId || 'N/A',
              serviceName,
              customerName,
              status: 'completed',
              amount,
              date: getDateString(service.completedAt || service.date || orderData?.createdAt),
              paymentStatus: 'paid',
              additionalServices: service.addedServices ? service.addedServices.map((addon: any) => ({
                name: addon.name || 'Additional Service',
                price: addon.price || 0,
                status: 'completed'
              })) : []
            });
          } catch (err) {
            console.error('Error processing completed service:', err);
          }
        }
      }
      
      // 2. Add accepted/confirmed services (fetch from orders collection)
      // FIX: Fetch all confirmed orders and filter by acceptedBy or serviceProviderId
      const confirmedOrdersSnapshot = await getDocs(
        query(
          collection(db, 'orders'),
          where('status', '==', 'confirmed')
        )
      );
      
      const providerConfirmedOrders = confirmedOrdersSnapshot.docs.filter(orderDoc => {
        const orderData = orderDoc.data();
        return orderData.acceptedBy === providerId || orderData.serviceProviderId === providerId;
      });
      
      console.log('Found', providerConfirmedOrders.length, 'confirmed orders for this provider');
      
      for (const orderDoc of providerConfirmedOrders) {
        const data = await fetchCompleteOrderData(orderDoc.id);
        if (data) {
          historyData.push({
            orderId: orderDoc.id,
            serviceName: data.serviceName,
            customerName: data.customerName,
            status: 'active',
            amount: data.totalAmount,
            date: getDateString(data.createdAt),
            paymentStatus: data.payment?.status || data.paymentStatus || 'pending',
            additionalServices: data.additionalServices?.map((service: any) => ({
              name: service.name || 'N/A',
              price: typeof service.price === 'number' ? service.price : 0,
              status: service.isPaid ? 'paid' : 'pending'
            }))
          });
        }
      }
      
      // 3. Add ignored requests
      if (providerData.ignoredRequests && Array.isArray(providerData.ignoredRequests)) {
        console.log('Processing', providerData.ignoredRequests.length, 'ignored requests');
        
        for (const orderId of providerData.ignoredRequests) {
          const data = await fetchCompleteOrderData(orderId);
          if (data) {
            historyData.push({
              orderId: orderId,
              serviceName: data.serviceName,
              customerName: data.customerName,
              status: 'ignored',
              amount: data.totalAmount,
              date: getDateString(data.createdAt),
              paymentStatus: data.payment?.status || data.paymentStatus || 'pending'
            });
          }
        }
      }
      
      // 4. Add cancelled services
      // FIX: Fetch all cancelled orders and filter by acceptedBy or serviceProviderId
      const cancelledOrdersSnapshot = await getDocs(
        query(
          collection(db, 'orders'),
          where('status', '==', 'cancelled')
        )
      );
      
      const providerCancelledOrders = cancelledOrdersSnapshot.docs.filter(orderDoc => {
        const orderData = orderDoc.data();
        return orderData.acceptedBy === providerId || orderData.serviceProviderId === providerId;
      });
      
      console.log('Found', providerCancelledOrders.length, 'cancelled orders for this provider');
      
      for (const orderDoc of providerCancelledOrders) {
        const data = await fetchCompleteOrderData(orderDoc.id);
        if (data) {
          historyData.push({
            orderId: orderDoc.id,
            serviceName: data.serviceName,
            customerName: data.customerName,
            status: 'cancelled',
            amount: data.totalAmount,
            date: getDateString(data.createdAt),
            paymentStatus: data.payment?.status || data.paymentStatus || 'failed',
            cancelReason: data.cancelReason
          });
        }
      }
      
      // Sort by date (newest first)
      historyData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      console.log('Service history loaded:', historyData.length, 'services');
      console.log('Sample history data:', historyData.slice(0, 2));
      setServiceHistory(historyData);
    } catch (error) {
      console.error('Error fetching service history:', error);
      toast.error('Failed to fetch service history from Firebase');
      setServiceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchProviderReviews = async (providerId: string) => {
    setLoadingReviews(true);
    try {
      // Fetch reviews from orders where this provider completed services
      // FIX: Fetch all completed orders and filter by acceptedBy or serviceProviderId
      const ordersSnapshot = await getDocs(
        query(
          collection(db, 'orders'),
          where('status', '==', 'completed')
        )
      );
      
      const providerCompletedOrders = ordersSnapshot.docs.filter(orderDoc => {
        const orderData = orderDoc.data();
        return orderData.acceptedBy === providerId || orderData.serviceProviderId === providerId;
      });
      
      const reviews: ProviderReview[] = [];
      
      for (const orderDoc of providerCompletedOrders) {
        const orderData = orderDoc.data();
        
        // Only include orders with customer reviews
        if (orderData.customerRating || orderData.customerReview) {
          reviews.push({
            id: orderDoc.id,
            providerId: providerId,
            orderId: orderDoc.id,
            customerId: orderData.userId || orderData.customerId || 'unknown',
            customerName: orderData.customerName || orderData.userName || 'Anonymous',
            customerEmail: orderData.customerEmail || orderData.userEmail || '',
            rating: orderData.customerRating || 0,
            review: orderData.customerReview || '',
            serviceType: orderData.serviceName || orderData.service?.name || 'Service',
            createdAt: orderData.reviewDate || orderData.completedAt || orderData.createdAt || new Date().toISOString(),
            isVerified: true, // All reviews from completed orders are verified
            response: orderData.providerResponse || undefined,
            respondedAt: orderData.providerResponseDate || undefined,
            status: orderData.reviewStatus || 'active',
            flagReason: orderData.reviewFlagReason || undefined
          });
        }
      }
      
      // Sort by date, newest first
      reviews.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setProviderReviews(reviews);
    } catch (error) {
      console.error('Error fetching provider reviews:', error);
      toast.error('Failed to fetch provider reviews');
      setProviderReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleViewDetails = async (provider: ServiceProvider) => {
    setSelectedProvider(provider);
    setShowDetailsModal(true);
    await fetchServiceHistory(provider.id);
  };
  
  const handleViewReviews = async (provider: ServiceProvider) => {
    setSelectedProvider(provider);
    setShowReviewsModal(true);
    await fetchProviderReviews(provider.id);
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = 
      provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.phone.includes(searchQuery) ||
      provider.specialization.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: providers.length,
    active: providers.filter(p => p.status === 'active').length,
    inactive: providers.filter(p => p.status === 'inactive').length,
    totalEarnings: providers.reduce((sum, p) => sum + p.totalEarnings, 0),
    avgRating: providers.reduce((sum, p) => sum + p.rating, 0) / providers.length || 0,
    feePaid: providers.filter(p => p.registrationFeePaid).length,
    feePending: providers.filter(p => !p.registrationFeePaid).length,
    totalCompleted: providers.reduce((sum, p) => sum + (p.completedServices || 0), 0),
    totalAccepted: providers.reduce((sum, p) => sum + (p.activeServices || 0), 0),
    totalCancelled: providers.reduce((sum, p) => sum + (p.cancelledServices || 0), 0)
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'ignored': return 'bg-gray-100 text-gray-800';
      case 'inactive': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-indigo-600 rounded-xl">
              <Users className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Service Providers</h2>
              <p className="text-gray-600 mt-1">Manage and track service provider performance</p>
            </div>
          </div>
          <button
            onClick={analyzeAllServiceProviders}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors shadow-md hover:shadow-lg flex items-center space-x-2"
          >
            <FileText className="h-5 w-5" />
            <span>Analyze All Providers</span>
          </button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
          <div className="bg-white rounded-xl p-4 border border-indigo-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Providers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-yellow-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.inactive}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold text-blue-600">₹{stats.totalEarnings.toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Rating</p>
                <p className="text-2xl font-bold text-purple-600">{stats.avgRating.toFixed(1)}</p>
              </div>
              <Star className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Additional Service Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div className="bg-white rounded-xl p-4 border border-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.totalCompleted}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-cyan-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Accepted</p>
                <p className="text-2xl font-bold text-cyan-600">{stats.totalAccepted}</p>
              </div>
              <Clock className="h-8 w-8 text-cyan-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-rose-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Cancelled</p>
                <p className="text-2xl font-bold text-rose-600">{stats.totalCancelled}</p>
              </div>
              <XCircle className="h-8 w-8 text-rose-600" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Reg Fee Paid</p>
                <p className="text-2xl font-bold text-green-600">{stats.feePaid}/{stats.total}</p>
                <p className="text-xs text-gray-500 mt-1">Pending: {stats.feePending}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or specialization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Providers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No service providers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Specialization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Earnings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reg Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProviders.map((provider) => (
                  <motion.tr
                    key={provider.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {provider.profileImage ? (
                            <img 
                              src={provider.profileImage} 
                              alt={provider.name}
                              className="h-12 w-12 rounded-full object-cover border-2 border-indigo-200"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center border-2 border-indigo-200">
                              <span className="text-indigo-600 font-medium text-lg">
                                {provider.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{provider.name}</div>
                          <div className="text-xs text-gray-400 font-mono">
                            Provider ID: {provider.providerId || provider.id}
                          </div>
                          <div className="text-sm text-gray-500">{provider.email}</div>
                          <div className="text-sm text-gray-500">{provider.phone}</div>
                          {(provider.documents?.aadharFront || provider.documents?.aadharBack) && (
                            <div className="mt-1 flex items-center space-x-1 text-xs text-green-600">
                              <FileText className="h-3 w-3" />
                              <span>Aadhar Uploaded</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {provider.specialization.map((spec, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800"
                          >
                            {typeof spec === 'string' ? spec : (spec as any)?.name || 'N/A'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Completed: {provider.completedServices}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <span>Accepted: {provider.activeServices}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span>Cancelled: {provider.cancelledServices}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Ban className="h-4 w-4 text-gray-600" />
                          <span>Ignored: {provider.ignoredServices}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{provider.totalEarnings.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        {provider.registrationFeePaid ? (
                          <>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✅ Paid ₹39
                            </span>
                            {provider.registrationFeePaidDate && (
                              <span className="text-xs text-gray-500">
                                {new Date(provider.registrationFeePaidDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            ⏳ Pending ₹39
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {provider.rating.toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(provider.status)}`}>
                        {provider.status.charAt(0).toUpperCase() + provider.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(provider)}
                          className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Details
                        </button>
                        <button
                          onClick={() => handleViewReviews(provider)}
                          className="inline-flex items-center px-3 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors"
                        >
                          <Star className="h-4 w-4 mr-2" />
                          Reviews
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedProvider && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {selectedProvider.profileImage ? (
                    <img 
                      src={selectedProvider.profileImage} 
                      alt={selectedProvider.name}
                      className="h-16 w-16 rounded-full border-4 border-white object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center border-4 border-white">
                      <span className="text-indigo-600 font-bold text-xl">
                        {selectedProvider.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedProvider.name}</h3>
                    <p className="text-indigo-100 text-sm">
                      {selectedProvider.specialization && selectedProvider.specialization.length > 0 
                        ? selectedProvider.specialization.slice(0, 3).join(', ') 
                        : 'Service Provider'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="p-6 space-y-6">
                  {/* Provider Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">Contact Information</h4>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3 bg-gray-50 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-indigo-600" />
                          <div className="flex-1">
                            <span className="text-xs text-gray-500 block">Provider ID</span>
                            <span className="text-sm font-mono text-gray-900 font-semibold">{selectedProvider.id}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Mail className="h-5 w-5 text-gray-400" />
                          <span className="text-sm text-gray-700">{selectedProvider.email}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Phone className="h-5 w-5 text-gray-400" />
                          <span className="text-sm text-gray-700">{selectedProvider.phone}</span>
                        </div>
                        {selectedProvider.address && (
                          <div className="flex items-center space-x-3">
                            <MapPin className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-700">
                              {typeof selectedProvider.address === 'string' 
                                ? selectedProvider.address 
                                : (selectedProvider.address as any).city || (selectedProvider.address as any).state || 'N/A'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center space-x-3">
                          <Calendar className="h-5 w-5 text-gray-400" />
                          <span className="text-sm text-gray-700">
                            Joined: {new Date(selectedProvider.joinedDate).toLocaleDateString()}
                          </span>
                        </div>
                        {selectedProvider.experience && (
                          <div className="flex items-center space-x-3">
                            <TrendingUp className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-700">Experience: {selectedProvider.experience}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Service Offerings */}
                      <div className="mt-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Service Portfolio</h4>
                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                          <p className="text-sm text-gray-600 mb-3">Services this provider can receive requests for:</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedProvider.specialization && selectedProvider.specialization.length > 0 ? (
                              selectedProvider.specialization.map((service, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-2 bg-indigo-100 text-indigo-800 rounded-lg text-sm font-medium flex items-center space-x-2"
                                >
                                  <Package className="h-4 w-4" />
                                  <span>{typeof service === 'string' ? service : (service as any)?.name || 'N/A'}</span>
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500 text-sm">No services configured</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">Performance Metrics</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Completed</p>
                              <p className="text-2xl font-bold text-green-600">{selectedProvider.completedServices || 0}</p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-600" />
                          </div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Accepted</p>
                              <p className="text-2xl font-bold text-blue-600">{selectedProvider.activeServices || 0}</p>
                            </div>
                            <Clock className="h-8 w-8 text-blue-600" />
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Cancelled</p>
                              <p className="text-2xl font-bold text-red-600">{selectedProvider.cancelledServices || 0}</p>
                            </div>
                            <XCircle className="h-8 w-8 text-red-600" />
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Ignored</p>
                              <p className="text-2xl font-bold text-gray-600">{selectedProvider.ignoredServices || 0}</p>
                            </div>
                            <Ban className="h-8 w-8 text-gray-600" />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-indigo-50 rounded-lg p-4 mt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Total Earnings</p>
                            <p className="text-2xl font-bold text-indigo-600">₹{(selectedProvider.totalEarnings || 0).toLocaleString()}</p>
                          </div>
                          <DollarSign className="h-8 w-8 text-indigo-600" />
                        </div>
                      </div>

                      {/* Registration Fee Status Card */}
                      <div className={`rounded-lg p-4 border-2 ${selectedProvider.registrationFeePaid ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <DollarSign className={`h-5 w-5 ${selectedProvider.registrationFeePaid ? 'text-green-600' : 'text-amber-600'}`} />
                              <p className="text-sm font-semibold text-gray-700">Registration Fee (₹39)</p>
                            </div>
                            <div className="flex items-center space-x-3">
                              <p className={`text-xl font-bold ${selectedProvider.registrationFeePaid ? 'text-green-600' : 'text-amber-600'}`}>
                                {selectedProvider.registrationFeePaid ? '✅ Verified & Paid' : '⏳ Payment Required'}
                              </p>
                            </div>
                            {selectedProvider.registrationFeePaid ? (
                              <div className="mt-2 space-y-1">
                                {selectedProvider.registrationFeePaidDate && (
                                  <p className="text-xs text-gray-600 flex items-center space-x-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>Paid: {new Date(selectedProvider.registrationFeePaidDate).toLocaleDateString('en-IN', { 
                                      day: '2-digit', 
                                      month: 'short', 
                                      year: 'numeric' 
                                    })}</span>
                                  </p>
                                )}
                                {selectedProvider.registrationFeeTransactionId && (
                                  <p className="text-xs text-gray-500 font-mono bg-white px-2 py-1 rounded">
                                    Txn: {selectedProvider.registrationFeeTransactionId}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-amber-700 mt-2 font-medium">
                                ⚠️ Provider must pay ₹39 to activate account and receive service requests
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Rating</p>
                            <div className="flex items-center space-x-2">
                              <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                              <p className="text-2xl font-bold text-gray-900">{selectedProvider.rating.toFixed(1)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Provider Details */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Complete Provider Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Personal Info */}
                      {selectedProvider.alternatePhone && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Alternate Phone</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.alternatePhone}</p>
                        </div>
                      )}
                      
                      {selectedProvider.city && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">City</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.city}</p>
                        </div>
                      )}
                      
                      {selectedProvider.state && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">State</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.state}</p>
                        </div>
                      )}
                      
                      {selectedProvider.pincode && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Pincode</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.pincode}</p>
                        </div>
                      )}
                      
                      {selectedProvider.experienceYears !== null && selectedProvider.experienceYears !== undefined && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Experience</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.experienceYears} years</p>
                        </div>
                      )}
                      
                      {selectedProvider.education && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Education</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.education}</p>
                        </div>
                      )}
                      
                      {selectedProvider.availability && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Availability</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.availability}</p>
                        </div>
                      )}
                      
                      {selectedProvider.workingHours && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Working Hours</p>
                          <p className="text-sm font-medium text-gray-900">{selectedProvider.workingHours}</p>
                        </div>
                      )}
                      
                      {selectedProvider.isVerified !== undefined && (
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-gray-500 mb-1">Verification Status</p>
                          <p className={`text-sm font-medium ${selectedProvider.isVerified ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedProvider.isVerified ? '✓ Verified' : '✗ Not Verified'}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Bio/Description */}
                    {selectedProvider.bio && (
                      <div className="mt-4 bg-white rounded-lg p-4 border border-blue-100">
                        <p className="text-xs text-gray-500 mb-2">Bio / Description</p>
                        <p className="text-sm text-gray-700">{selectedProvider.bio}</p>
                      </div>
                    )}
                    
                    {/* Skills */}
                    {selectedProvider.skills && selectedProvider.skills.length > 0 && (
                      <div className="mt-4 bg-white rounded-lg p-4 border border-blue-100">
                        <p className="text-xs text-gray-500 mb-2">Skills</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedProvider.skills.map((skill, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Languages */}
                    {selectedProvider.languages && selectedProvider.languages.length > 0 && (
                      <div className="mt-4 bg-white rounded-lg p-4 border border-blue-100">
                        <p className="text-xs text-gray-500 mb-2">Languages</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedProvider.languages.map((lang, idx) => (
                            <span key={idx} className="px-2 py-1 bg-cyan-100 text-cyan-800 rounded text-xs font-medium">
                              {lang}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Bank Details */}
                    {(selectedProvider.bankName || selectedProvider.accountNumber || selectedProvider.ifscCode || selectedProvider.upiId) && (
                      <div className="mt-4 bg-white rounded-lg p-4 border border-blue-100">
                        <p className="text-xs text-gray-500 mb-3 font-semibold">Bank Details</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedProvider.bankName && (
                            <div>
                              <p className="text-xs text-gray-500">Bank Name</p>
                              <p className="text-sm font-medium text-gray-900">{selectedProvider.bankName}</p>
                            </div>
                          )}
                          {selectedProvider.accountNumber && (
                            <div>
                              <p className="text-xs text-gray-500">Account Number</p>
                              <p className="text-sm font-medium text-gray-900 font-mono">{selectedProvider.accountNumber}</p>
                            </div>
                          )}
                          {selectedProvider.ifscCode && (
                            <div>
                              <p className="text-xs text-gray-500">IFSC Code</p>
                              <p className="text-sm font-medium text-gray-900 font-mono">{selectedProvider.ifscCode}</p>
                            </div>
                          )}
                          {selectedProvider.upiId && (
                            <div>
                              <p className="text-xs text-gray-500">UPI ID</p>
                              <p className="text-sm font-medium text-gray-900 font-mono">{selectedProvider.upiId}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Rejection Reason if any */}
                    {selectedProvider.rejectionReason && (
                      <div className="mt-4 bg-red-50 rounded-lg p-4 border border-red-200">
                        <p className="text-xs text-red-600 mb-2 font-semibold">Rejection Reason</p>
                        <p className="text-sm text-red-700">{selectedProvider.rejectionReason}</p>
                      </div>
                    )}
                  </div>

                  {/* Document Verification & Status Management */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border-2 border-purple-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-purple-600" />
                      Account Verification & Management
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Current Status */}
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <p className="text-sm font-medium text-gray-700 mb-2">Current Status</p>
                          <span className={`inline-flex px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(selectedProvider.status)}`}>
                            {selectedProvider.status.charAt(0).toUpperCase() + selectedProvider.status.slice(1)}
                          </span>
                        </div>

                        {/* Status Change Controls */}
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <p className="text-sm font-medium text-gray-700 mb-3">Change Status</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => updateProviderStatus(selectedProvider.id, 'active', true)}
                              disabled={updatingStatus || selectedProvider.status === 'active'}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedProvider.status === 'active'
                                  ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                            >
                              {updatingStatus ? 'Updating...' : '✓ Activate & Verify'}
                            </button>
                            <button
                              onClick={() => updateProviderStatus(selectedProvider.id, 'inactive')}
                              disabled={updatingStatus || selectedProvider.status === 'inactive'}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedProvider.status === 'inactive'
                                  ? 'bg-gray-100 text-gray-800 cursor-not-allowed'
                                  : 'bg-gray-600 text-white hover:bg-gray-700'
                              }`}
                            >
                              {updatingStatus ? 'Updating...' : 'Deactivate'}
                            </button>
                            <button
                              onClick={() => updateProviderStatus(selectedProvider.id, 'suspended')}
                              disabled={updatingStatus || selectedProvider.status === 'suspended'}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedProvider.status === 'suspended'
                                  ? 'bg-red-100 text-red-800 cursor-not-allowed'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              {updatingStatus ? 'Updating...' : '⊗ Suspend'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Documents Section - All Document Types */}
                      <div className="bg-white rounded-lg p-4 border border-purple-200">
                        <p className="text-sm font-medium text-gray-700 mb-3">Verification Documents</p>
                        {selectedProvider.documents ? (
                          <div className="space-y-2">
                            {/* Aadhar Front */}
                            {selectedProvider.documents.aadharFront && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">📄 Aadhar Card (Front)</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.aadharFront, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* Aadhar Back */}
                            {selectedProvider.documents.aadharBack && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">📄 Aadhar Card (Back)</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.aadharBack, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* PAN Card */}
                            {selectedProvider.documents.panCard && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">🏦 PAN Card</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.panCard, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* ID Proof */}
                            {selectedProvider.documents.idProof && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">🆔 ID Proof</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.idProof, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* Certification */}
                            {selectedProvider.documents.certification && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">📜 Certification</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.certification, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* Police Clearance */}
                            {selectedProvider.documents.policeClearance && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">👮 Police Clearance</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.policeClearance, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* Education Certificate */}
                            {selectedProvider.documents.educationCertificate && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">🎓 Education Certificate</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.educationCertificate, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* Experience Certificate */}
                            {selectedProvider.documents.experienceCertificate && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">💼 Experience Certificate</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.experienceCertificate, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            {/* Bank Proof */}
                            {selectedProvider.documents.bankProof && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">🏦 Bank Proof</span>
                                <button
                                  onClick={() => window.open(selectedProvider.documents?.bankProof, '_blank')}
                                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                >
                                  View
                                </button>
                              </div>
                            )}
                            
                            {/* No documents message */}
                            {!selectedProvider.documents.aadharFront && 
                             !selectedProvider.documents.aadharBack &&
                             !selectedProvider.documents.panCard &&
                             !selectedProvider.documents.idProof &&
                             !selectedProvider.documents.certification &&
                             !selectedProvider.documents.policeClearance &&
                             !selectedProvider.documents.educationCertificate &&
                             !selectedProvider.documents.experienceCertificate &&
                             !selectedProvider.documents.bankProof && (
                              <p className="text-sm text-gray-500">No documents uploaded yet</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No documents uploaded yet</p>
                        )}
                        
                        {selectedProvider.documents?.aadharFront && selectedProvider.documents?.aadharBack && (
                          <button
                            onClick={() => setShowDocumentsModal(true)}
                            className="mt-3 w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                          >
                            View All Documents
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Verification Info */}
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> After verifying the provider's documents, click "Activate & Verify" to enable their account. 
                        New accounts are set to "inactive" by default until manually verified.
                      </p>
                    </div>
                  </div>

                  {/* Service History */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Service History</h4>
                    {loadingHistory ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {serviceHistory.map((service) => (
                          <div
                            key={service.orderId}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <Package className="h-5 w-5 text-gray-400" />
                                  <span className="font-medium text-gray-900">{service.serviceName}</span>
                                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(service.status)}`}>
                                    {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                                  </span>
                                  <span className={`px-2 py-1 text-xs rounded-full ${getPaymentStatusColor(service.paymentStatus)}`}>
                                    {service.paymentStatus.charAt(0).toUpperCase() + service.paymentStatus.slice(1)}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600 mb-2">
                                  <div>
                                    <span className="font-medium">Order ID:</span> #{service.orderId.slice(-8)}
                                  </div>
                                  <div>
                                    <span className="font-medium">Customer:</span> {service.customerName}
                                  </div>
                                  <div>
                                    <span className="font-medium">Date:</span> {new Date(service.date).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </div>
                                  <div>
                                    <span className="font-medium">Time:</span> {new Date(service.date).toLocaleTimeString('en-IN', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600">
                                  <div className="font-medium text-gray-900">
                                    Amount: ₹{service.amount.toLocaleString()}
                                  </div>
                                </div>
                                
                                {service.additionalServices && service.additionalServices.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Additional Services:</p>
                                    <div className="space-y-1">
                                      {service.additionalServices.map((addService, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-sm">
                                          <span className="text-gray-600">• {addService.name}</span>
                                          <span className="text-gray-900 font-medium">₹{addService.price}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {service.cancelReason && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-start space-x-2 text-sm">
                                      <FileText className="h-4 w-4 text-red-600 mt-0.5" />
                                      <div>
                                        <span className="font-medium text-red-600">Cancel Reason:</span>
                                        <p className="text-gray-600 mt-1">{service.cancelReason}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Documents Verification Modal */}
      <AnimatePresence>
        {showDocumentsModal && selectedProvider && selectedProvider.documents && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowDocumentsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white">Verification Documents</h3>
                  <p className="text-purple-100 text-sm">{selectedProvider.name}</p>
                </div>
                <button
                  onClick={() => setShowDocumentsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
                <div className="space-y-6">
                  {/* Aadhar Card Front */}
                  {selectedProvider.documents.aadharFront && (
                    <div className="border-2 border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-indigo-600" />
                        Aadhar Card - Front Side
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <img
                          src={selectedProvider.documents.aadharFront}
                          alt="Aadhar Front"
                          className="w-full h-auto rounded shadow-md"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </div>
                      <div className="mt-3 flex justify-end">
                        <a
                          href={selectedProvider.documents.aadharFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          Open in New Tab
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Aadhar Card Back */}
                  {selectedProvider.documents.aadharBack && (
                    <div className="border-2 border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-indigo-600" />
                        Aadhar Card - Back Side
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <img
                          src={selectedProvider.documents.aadharBack}
                          alt="Aadhar Back"
                          className="w-full h-auto rounded shadow-md"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </div>
                      <div className="mt-3 flex justify-end">
                        <a
                          href={selectedProvider.documents.aadharBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          Open in New Tab
                        </a>
                      </div>
                    </div>
                  )}

                  {/* PAN Card if available */}
                  {selectedProvider.documents.panCard && (
                    <div className="border-2 border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-indigo-600" />
                        PAN Card
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <img
                          src={selectedProvider.documents.panCard}
                          alt="PAN Card"
                          className="w-full h-auto rounded shadow-md"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </div>
                      <div className="mt-3 flex justify-end">
                        <a
                          href={selectedProvider.documents.panCard}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          Open in New Tab
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Verification Actions */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Verification Actions</h4>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => {
                          updateProviderStatus(selectedProvider.id, 'active', true);
                          setShowDocumentsModal(false);
                        }}
                        disabled={updatingStatus || selectedProvider.status === 'active'}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        ✓ Verify & Activate Account
                      </button>
                      <button
                        onClick={() => {
                          updateProviderStatus(selectedProvider.id, 'suspended');
                          setShowDocumentsModal(false);
                        }}
                        disabled={updatingStatus}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        ⊗ Reject & Suspend
                      </button>
                    </div>
                    <p className="mt-4 text-sm text-gray-600">
                      After reviewing the documents, you can verify and activate the provider's account or reject if documents are invalid.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews Modal */}
      <AnimatePresence>
        {showReviewsModal && selectedProvider && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReviewsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {selectedProvider.profileImage ? (
                      <img 
                        src={selectedProvider.profileImage} 
                        alt={selectedProvider.name}
                        className="h-16 w-16 rounded-full object-cover border-4 border-white"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-white bg-opacity-30 flex items-center justify-center border-4 border-white">
                        <span className="text-2xl font-bold text-white">
                          {selectedProvider.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    )}
                    <div>
                      <h2 className="text-2xl font-bold">{selectedProvider.name}</h2>
                      <p className="text-yellow-100">Customer Reviews & Ratings</p>
                      <div className="flex items-center space-x-3 mt-2">
                        <div className="flex items-center space-x-1">
                          <Star className="h-5 w-5 fill-white" />
                          <span className="text-lg font-bold">{selectedProvider.rating.toFixed(1)}</span>
                        </div>
                        <span className="text-yellow-100">•</span>
                        <span className="text-yellow-100">{providerReviews.length} reviews</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReviewsModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {loadingReviews ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
                  </div>
                ) : providerReviews.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No reviews yet</p>
                    <p className="text-gray-500 text-sm mt-2">
                      Reviews will appear here once customers complete their services
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {providerReviews.map((review) => (
                      <motion.div
                        key={review.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                                <span className="text-white font-bold">
                                  {review.customerName.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">{review.customerName}</h4>
                                <p className="text-sm text-gray-500">{review.customerEmail}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 mb-3">
                              <div className="flex items-center space-x-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-4 w-4 ${
                                      i < review.rating
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-medium text-gray-900">{review.rating}.0</span>
                              {review.isVerified && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Verified
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">
                              {new Date(review.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            <span className={`mt-2 inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              review.status === 'active' 
                                ? 'bg-green-100 text-green-800'
                                : review.status === 'hidden'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                            </span>
                          </div>
                        </div>

                        <div className="mb-3">
                          <span className="inline-block px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                            {review.serviceType}
                          </span>
                        </div>

                        {review.review && (
                          <div className="bg-white rounded-lg p-4 mb-3">
                            <p className="text-gray-700 leading-relaxed">{review.review}</p>
                          </div>
                        )}

                        {review.response && (
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <span className="text-yellow-600 font-medium text-sm">Provider Response:</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-gray-700 text-sm leading-relaxed">{review.response}</p>
                                {review.respondedAt && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    Responded on {new Date(review.respondedAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {review.status === 'flagged' && review.flagReason && (
                          <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4 mt-3">
                            <div className="flex items-start space-x-3">
                              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-red-800">Flagged for review</p>
                                <p className="text-sm text-red-700 mt-1">{review.flagReason}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                          <div className="text-xs text-gray-500">
                            Order ID: <span className="font-mono">{review.orderId}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {review.status === 'active' && (
                              <button
                                onClick={() => {
                                  // TODO: Implement hide review functionality
                                  toast.info('Hide review functionality coming soon');
                                }}
                                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                Hide Review
                              </button>
                            )}
                            {review.status === 'hidden' && (
                              <button
                                onClick={() => {
                                  // TODO: Implement show review functionality
                                  toast.info('Show review functionality coming soon');
                                }}
                                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                              >
                                Show Review
                              </button>
                            )}
                            {review.status !== 'flagged' && (
                              <button
                                onClick={() => {
                                  // TODO: Implement flag review functionality
                                  toast.info('Flag review functionality coming soon');
                                }}
                                className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                              >
                                <Ban className="h-3 w-3 inline mr-1" />
                                Flag
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Total Reviews: <span className="font-semibold">{providerReviews.length}</span>
                  </div>
                  <button
                    onClick={() => setShowReviewsModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
